import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getDataForDeleteTrip } from './TripDashboard.helpers'
import { deleteTrip } from './TripDashboard.service'
import type { TripData } from './TripDashboard.types'

interface DeleteTripDialogPayload {
  trip: TripData
}

interface DeleteTripDialogProps {
  payload: DeleteTripDialogPayload
  open: boolean
  onAction: (updatedTrip: TripData, action: string) => void
  onClose: () => void
}

const DeleteTripDialog = ({ payload, open, onAction, onClose }: DeleteTripDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, formState, setError } = useForm()
  const [deletionConfirmed, setDeletionConfirmed] = useState(false)
  const deleteTripMutation = useMutation({
    mutationFn: deleteTrip,
  })

  useEffect(() => {
    setDeletionConfirmed(false)
  }, [payload])

  const onSubmit: SubmitHandler<object> = async (): Promise<void> => {
    await handleFormSubmission(() => deleteTripMutation.mutateAsync(payload.trip.entityId), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction(getDataForDeleteTrip(payload.trip), 'delete')
        analyticsEvent('polestar_cs_trip_deleted')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.trip.tripNumber} — ${t('screens.cs.trip_dashboard.card.actions.delete')}`}
      actions={
        <Button
          type="submit"
          color="error"
          variant="contained"
          loading={formState.isSubmitting}
          disabled={!deletionConfirmed}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.cs.trip_dashboard.delete_trip.delete')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="xs"
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Typography>
        {t('screens.cs.trip_dashboard.delete_trip.confirmation')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            value={deletionConfirmed}
            checked={deletionConfirmed}
            onChange={(): void => setDeletionConfirmed(!deletionConfirmed)}
          />
        }
        label={t('shared.manager.deletion_confirm_checkbox')}
      />
    </KNDialog>
  )
}

export default DeleteTripDialog
